import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion, useMotionValueEvent, useScroll } from "framer-motion";
import { Heart, Menu, Search, ShoppingBag, User, X } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { formatPrice } from "@/data/products";
import { useCatalog } from "@/data/catalog";

const NAV = [
  { label: "Shop", to: "/shop", id: "shop" },
  { label: "Men", to: "/shop?cat=men", id: "men" },
  { label: "Women", to: "/shop?cat=women", id: "women" },
  { label: "Collections", to: "/#collections", id: "collections" },
  { label: "Community", to: "/community", id: "community" },
  { label: "New Arrivals", to: "/#new", id: "new" },
  { label: "Best Sellers", to: "/#best", id: "best" },
  { label: "About", to: "/#story", id: "about" },
];

const SearchOverlay = ({ open, onClose }) => {
  const [q, setQ] = useState("");
  const navigate = useNavigate();
  const { products } = useCatalog();
  const results = q.trim()
    ? products.filter((p) => `${p.name} ${p.collection} ${p.category}`.toLowerCase().includes(q.toLowerCase()))
    : [];

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="search-overlay"
          className="fixed inset-0 z-[80] bg-obsidian/95 backdrop-blur-xl"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="max-w-4xl mx-auto px-6 pt-28">
            <button
              data-testid="search-close-button"
              onClick={onClose}
              className="absolute top-6 right-6 text-stone/70 hover:text-stone transition-colors"
              aria-label="Close search"
            >
              <X size={26} />
            </button>
            <motion.div initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.1 }}>
              <input
                data-testid="search-input"
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="SEARCH THE RANGE"
                className="w-full bg-transparent border-b border-stone/20 pb-4 font-display text-3xl md:text-5xl uppercase tracking-wide placeholder:text-stone/25 focus:outline-none focus:border-mist transition-colors"
              />
              <div className="mt-10 grid gap-3 max-h-[50vh] overflow-y-auto">
                {results.map((p) => (
                  <button
                    key={p.id}
                    data-testid={`search-result-${p.id}`}
                    onClick={() => {
                      onClose();
                      navigate(`/product/${p.id}`);
                    }}
                    className="flex items-center gap-5 text-left group border border-transparent hover:border-stone/15 p-3 transition-colors"
                  >
                    <img src={p.images[0]} alt={p.name} className="w-14 h-16 object-cover" />
                    <div>
                      <p className="font-display uppercase tracking-wide group-hover:text-mist transition-colors">{p.name}</p>
                      <p className="text-sm text-stone/50">
                        {p.collection} — {formatPrice(p.price)}
                      </p>
                    </div>
                  </button>
                ))}
                {q.trim() && results.length === 0 && (
                  <p data-testid="search-no-results" className="text-stone/50 py-8">
                    Nothing found for “{q}”. Try “run”, “tee” or “training”.
                  </p>
                )}
              </div>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const { count, setDrawerOpen, wishlist } = useCart();
  const location = useLocation();
  const { scrollY } = useScroll();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Premium pattern: header slides away when scrolling down, returns on scroll up.
  useMotionValueEvent(scrollY, "change", (y) => {
    const prev = scrollY.getPrevious() ?? 0;
    if (menuOpen || searchOpen) return;
    setHidden(y > prev && y > 160);
  });

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
  }, [location]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 inset-x-0 z-[70] transition-all duration-500 ${
          hidden ? "-translate-y-full" : "translate-y-0"
        } ${
          scrolled
            ? "bg-obsidian/90 backdrop-blur-xl border-b border-stone/10 py-3"
            : "bg-transparent border-b border-transparent py-6"
        }`}
      >
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 flex items-center justify-between">
          <Link to="/" data-testid="header-logo" className="relative z-10 shrink-0" aria-label="MVM home">
            <img src="/assets/brand/logo-mark.svg" alt="MVM" className={`transition-all duration-500 ${scrolled ? "h-8 md:h-11" : "h-10 md:h-16"}`} />
          </Link>

          <nav className="hidden xl:flex items-center gap-8" aria-label="Primary">
            {NAV.map((item) => (
              <Link
                key={item.id}
                to={item.to}
                data-testid={`nav-${item.id}`}
                className="link-sweep text-[13px] font-medium tracking-[0.18em] uppercase text-stone/80 hover:text-stone transition-colors"
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-4 md:gap-5">
            <button data-testid="search-button" onClick={() => setSearchOpen(true)} aria-label="Search" className="text-stone/80 hover:text-mist transition-colors">
              <Search size={20} strokeWidth={1.6} />
            </button>
            <button data-testid="account-button" aria-label="Account" className="hidden sm:block text-stone/80 hover:text-mist transition-colors">
              <User size={20} strokeWidth={1.6} />
            </button>
            <button data-testid="wishlist-button" aria-label="Wishlist" className="hidden sm:block relative text-stone/80 hover:text-mist transition-colors">
              <Heart size={20} strokeWidth={1.6} />
              {wishlist.length > 0 && <span className="absolute -top-1.5 -right-1.5 h-2 w-2 rounded-full bg-mist" />}
            </button>
            <button
              data-testid="cart-button"
              onClick={() => setDrawerOpen(true)}
              aria-label="Shopping bag"
              className="relative text-stone/80 hover:text-mist transition-colors"
            >
              <ShoppingBag size={20} strokeWidth={1.6} />
              {count > 0 && (
                <span data-testid="cart-count-badge" className="absolute -top-2 -right-2 bg-stone text-obsidian text-[10px] font-semibold h-4 min-w-4 px-1 flex items-center justify-center">
                  {count}
                </span>
              )}
            </button>
            <button
              data-testid="mobile-menu-button"
              onClick={() => setMenuOpen(true)}
              aria-label="Open menu"
              className="xl:hidden text-stone"
            >
              <Menu size={24} strokeWidth={1.6} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            data-testid="mobile-menu"
            className="fixed inset-0 z-[90] bg-obsidian flex flex-col"
            initial={{ clipPath: "inset(0 0 100% 0)" }}
            animate={{ clipPath: "inset(0 0 0% 0)" }}
            exit={{ clipPath: "inset(0 0 100% 0)" }}
            transition={{ duration: 0.6, ease: [0.76, 0, 0.24, 1] }}
          >
            <div className="flex items-center justify-between px-6 py-6">
              <img src="/assets/brand/logo-mark.svg" alt="MVM" className="h-8" />
              <button data-testid="mobile-menu-close" onClick={() => setMenuOpen(false)} aria-label="Close menu" className="text-stone">
                <X size={26} />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-8 gap-2" aria-label="Mobile">
              {NAV.map((item, i) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.25 + i * 0.06 }}
                >
                  <Link
                    to={item.to}
                    data-testid={`mobile-nav-${item.id}`}
                    className="font-display text-4xl sm:text-5xl uppercase font-medium text-stone/85 hover:text-mist transition-colors leading-tight"
                  >
                    {item.label}
                  </Link>
                </motion.div>
              ))}
            </nav>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
              className="px-8 pb-10 text-xs tracking-[0.3em] uppercase text-stone/40"
            >
              MVM Athletics — The only competition that matters
            </motion.p>
          </motion.div>
        )}
      </AnimatePresence>

      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
};
